<template>
    <div class="min-h-screen flex bg-slate-50 overflow-hidden font-sans">
        <!-- Background Blurs -->
        <div class="absolute inset-0 overflow-hidden pointer-events-none z-0">
            <div class="glass-blob blob-1"></div>
            <div class="glass-blob blob-2"></div>
            <div class="glass-blob blob-3"></div>
        </div>

        <!-- Left side - Form -->
        <div
            class="w-full lg:w-1/2 flex items-center justify-center py-12 bg-white relative z-10"
            :style="{ paddingLeft: normalizeSize(90), paddingRight: normalizeSize(90) }"
        >
            <!-- Language Switcher -->
            <div class="absolute top-8 right-8 z-50">
                <LanguageSwitcher />
            </div>

            <div class="w-full h-full">
                <!-- Progress Indicator -->
                <div class="flex items-center my-12 justify-center" :style="{ gap: normalizeSize(50, true) }">
                    <div class="h-1.5 rounded-full bg-[#7D2DBD] bg-opacity-30" :style="{ width: normalizeSize(160, true) }"></div>
                    <div class="h-1.5 rounded-full bg-[#7D2DBD] bg-opacity-30" :style="{ width: normalizeSize(160, true) }"></div>
                    <div class="h-1.5 rounded-full bg-[#7D2DBD] bg-opacity-30" :style="{ width: normalizeSize(160, true) }"></div>
                </div>

                <div class="mb-12 text-center">
                    <h2 class="poppinsRegular text-slate-800 tracking-tight mb-4" :style="{ fontSize: normalizeFontSize(20) }">
                        {{ $t('registration.create_account') }}
                    </h2>
                    <!--p class="text-slate-500 font-medium text-lg leading-relaxed">
                            {{ $t('registration.configure_access') }}
                        </p-->
                </div>

                <!-- Server Errors -->
                <div
                    v-if="serverErrors.general || serverErrors.email"
                    class="mb-8 p-3 bg-rose-50 border border-rose-100 rounded-xl text-rose-600 text-sm flex flex-col gap-1"
                >
                    <div v-if="serverErrors.general" class="flex items-start">
                        <span class="mr-2">⚠️</span>
                        <span>{{ serverErrors.general }}</span>
                    </div>
                    <div v-if="serverErrors.email" class="flex items-start">
                        <span class="mr-2">📧</span>
                        <span>{{ Array.isArray(serverErrors.email) ? serverErrors.email[0] : serverErrors.email }}</span>
                    </div>
                </div>

                <div class="space-y-8">
                    <!-- URL Field -->
                    <div class="space-y-3 group/url">
                        <label class="text-[#021832] poppinsRegular ml-1" :style="{ fontSize: normalizeFontSize(15) }">
                            {{ $t('registration.subdomain_label') }}<span class="text-[#7D2DBD]">*</span>
                        </label>
                        <p class="text-[#021832] poppinsRegular ml-1" :style="{ fontSize: normalizeFontSize(12) }">
                            {{ $t('registration.subdomain_description') }}
                        </p>
                        <div class="flex items-end gap-3">
                            <div
                                class="flex-1 flex items-center bg-white rounded-xl border border-[#9c9af880] px-3 transition-all duration-200 focus-within:border-[#9c9af8b2]"
                                :style="{ height: normalizeSize(50, true) }"
                            >
                                <input
                                    type="text"
                                    v-model="localFormData.subdomain"
                                    @input="onSubdomainInput"
                                    :placeholder="$t('registration.placeholders.subdomain')"
                                    class="w-full bg-transparent border-none outline-none text-[#0f172a] p-0 placeholder-slate-400 poppinsRegular"
                                    :style="{ fontSize: normalizeFontSize(15) }"
                                />
                            </div>
                            <span class="text-[#021832] poppinsLight mb-3" :style="{ fontSize: normalizeFontSize(15) }"
                                >.playpro.fr</span
                            >
                        </div>

                        <!-- Subdomain Preview -->
                        <!--div v-if="localFormData.subdomain" class="bg-[#F8FAFC] rounded-[24px] border border-[#F1F5F9] p-6">
                            <div class="flex justify-between items-center">
                                <span class="text-[10px] text-slate-400 font-black uppercase tracking-widest">{{
                                    $t('registration.your_store')
                                }}</span>
                                <span class="text-primary poppinsBold text-[15px]"
                                    >{{ localFormData.subdomain.toLowerCase() }}.playpro.fr</span
                                >
                            </div>
                            <div class="flex justify-between items-center pt-4 border-t border-[#E2E8F0]">
                                <span class="text-[10px] text-slate-400 font-black uppercase tracking-widest">{{
                                    $t('registration.administration')
                                }}</span>
                                <span class="text-primary poppinsBold text-[15px]"
                                    >{{ localFormData.subdomain.toLowerCase() }}-bo.playpro.fr</span
                                >
                            </div>
                        </div-->

                        <p v-if="errors.subdomain || serverErrors.subdomain" class="text-[11px] text-[#ed4b9e] ml-1">
                            {{
                                errors.subdomain ||
                                (Array.isArray(serverErrors.subdomain) ? serverErrors.subdomain[0] : serverErrors.subdomain)
                            }}
                        </p>
                        <p v-else class="poppinsLight text-[11px] text-slate-400 ml-1">
                            {{ $t('registration.subdomain_hint') }}
                        </p>
                    </div>

                    <!-- Password Field -->
                    <div class="space-y-3 group/pass">
                        <label class="text-[#021832] poppinsRegular ml-1" :style="{ fontSize: normalizeFontSize(15) }">
                            {{ $t('registration.password_label') }}<span class="text-[#7D2DBD]">*</span>
                        </label>
                        <div
                            class="flex items-center bg-white rounded-xl border px-3 transition-all duration-200 focus-within:border-[#9c9af8b2]"
                            :class="errors.password ? 'border-[#ed4b9e]' : 'border-[#9c9af880]'"
                            :style="{ height: normalizeSize(50, true) }"
                        >
                            <input
                                :type="showPassword ? 'text' : 'password'"
                                v-model="localFormData.password"
                                @input="validatePasswords"
                                :placeholder="$t('registration.placeholders.password')"
                                class="w-full bg-transparent border-none outline-none text-[#0f172a] p-0 placeholder-slate-400 poppinsRegular"
                                :style="{ fontSize: normalizeFontSize(15) }"
                            />
                            <button
                                type="button"
                                class="text-slate-400 hover:text-[#7D2DBD] transition-colors"
                                @click="showPassword = !showPassword"
                            >
                                <img
                                    :src="isDark ? '/media/svg-cahe-mdp-white.svg' : '/media/svg-cahe-mdp.svg'"
                                    alt="Toggle Password"
                                    class="w-6 h-6"
                                />
                            </button>
                        </div>
                        <p v-if="errors.password" class="text-[11px] text-[#ed4b9e] ml-1">{{ errors.password }}</p>
                    </div>

                    <!-- Confirm Password Field -->
                    <div class="space-y-3 group/cpass">
                        <label class="text-[#021832] poppinsRegular ml-1" :style="{ fontSize: normalizeFontSize(15) }">
                            {{ $t('registration.confirm_password_label') }}<span class="text-[#7D2DBD]">*</span>
                        </label>
                        <div
                            class="flex items-center bg-white rounded-xl border px-3 transition-all duration-200 focus-within:border-[#9c9af8b2]"
                            :class="errors.passwordConfirmation ? 'border-[#ed4b9e]' : 'border-[#9c9af880]'"
                            :style="{ height: normalizeSize(50, true) }"
                        >
                            <input
                                :type="showConfirmPassword ? 'text' : 'password'"
                                v-model="localFormData.passwordConfirmation"
                                @input="validatePasswords"
                                placeholder="••••••••"
                                class="w-full bg-transparent border-none outline-none text-[#0f172a] p-0 placeholder-slate-400 poppinsRegular"
                                :style="{ fontSize: normalizeFontSize(15) }"
                            />
                            <button
                                type="button"
                                class="text-slate-400 hover:text-[#7D2DBD] transition-colors"
                                @click="showConfirmPassword = !showConfirmPassword"
                            >
                                <img
                                    :src="isDark ? '/media/svg-cahe-mdp-white.svg' : '/media/svg-cahe-mdp.svg'"
                                    alt="Toggle Password"
                                    class="w-6 h-6"
                                />
                            </button>
                        </div>
                        <p v-if="errors.passwordConfirmation" class="text-[11px] text-[#ed4b9e] ml-1">
                            {{ errors.passwordConfirmation }}
                        </p>
                    </div>

                    <!-- Consent Checkboxes -->
                    <div class="space-y-4 pt-6">
                        <!-- Manual Checkbox 1 -->
                        <div
                            class="inline-flex items-center justify-start gap-3 cursor-pointer group"
                            @click="localFormData.acceptTerms = !localFormData.acceptTerms"
                        >
                            <div
                                class="w-5 h-5 rounded border flex items-center justify-center transition-all duration-200 mt-0.5 shrink-0"
                                :class="[
                                    localFormData.acceptTerms
                                        ? 'bg-[#7D2DBD] border-[#7D2DBD]'
                                        : 'bg-white border-slate-300 group-hover:border-[#7D2DBD]',
                                    errors.acceptTerms ? 'border-[#ed4b9e]' : '',
                                ]"
                            >
                                <svg
                                    v-if="localFormData.acceptTerms"
                                    width="12"
                                    height="12"
                                    viewBox="0 0 12 12"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M10 3L4.5 8.5L2 6"
                                        stroke="white"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                            </div>
                            <span class="text-sm text-[#021832] poppinsRegular leading-tight select-none">
                                <span
                                    v-html="
                                        $t('registration.terms_label', {
                                            cgvu: `<span class='underline poppinsRegular cursor-pointer'>CGVU</span>`,
                                            privacy: `<span class='underline poppinsRegular cursor-pointer'>${$t(
                                                'registration.privacy_policy'
                                            )}</span>`,
                                        })
                                    "
                                ></span>
                            </span>
                        </div>

                        <!-- Manual Checkbox 2 -->
                        <div
                            class="inline-flex items-center justify-start gap-3 cursor-pointer group"
                            @click="localFormData.acceptOffers = !localFormData.acceptOffers"
                        >
                            <div
                                class="w-5 h-5 rounded border flex items-center justify-center transition-all duration-200 mt-0.5 shrink-0"
                                :class="
                                    localFormData.acceptOffers
                                        ? 'bg-[#7D2DBD] border-[#7D2DBD]'
                                        : 'bg-white border-slate-300 group-hover:border-[#7D2DBD]'
                                "
                            >
                                <svg
                                    v-if="localFormData.acceptOffers"
                                    width="12"
                                    height="12"
                                    viewBox="0 0 12 12"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M10 3L4.5 8.5L2 6"
                                        stroke="white"
                                        stroke-width="2"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                            </div>
                            <span class="text-sm text-[#021832] poppinsRegular leading-tight select-none">
                                {{ $t('registration.offers_label') }}
                            </span>
                        </div>

                        <p v-if="errors.acceptTerms" class="text-[11px] text-[#ed4b9e] ml-1">
                            {{ errors.acceptTerms }}
                        </p>
                    </div>
                </div>

                <div class="mt-12 flex flex-col items-center gap-4">
                    <button
                        class="w-[80%] mx-auto bg-gradient-to-r from-[#40216F] to-[#182471] text-white rounded-[10px] border-[1.83px] border-white/50 hover:shadow-[inset_0_0_0_1000px_#00000066] transition-all duration-300 poppinsMedium disabled:opacity-65 disabled:pointer-events-none flex items-center justify-center gap-3"
                        @click="submitForm"
                        :disabled="!isFormValid || isSubmitting"
                        :style="{ height: normalizeSize(56, true), fontSize: normalizeFontSize(16) }"
                    >
                        <span
                            v-if="isSubmitting"
                            class="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"
                        ></span>
                        {{ isSubmitting ? $t('registration.creating_account') : $t('registration.finish_registration') }}
                    </button>
                    <button
                        class="text-slate-400 hover:text-slate-600 transition-colors poppinsMedium"
                        @click="goBack"
                        :style="{ fontSize: normalizeFontSize(14) }"
                        :disabled="isSubmitting"
                    >
                        {{ $t('registration.back') }}
                    </button>
                </div>
            </div>
        </div>

        <!-- Right side - Final Social Proof (Glass) -->
        <div class="hidden lg:flex w-full lg:w-1/2 relative items-center justify-center overflow-hidden bg-[#0f1035]">
            <!-- Background Image -->
            <img
                src="/media/registerTenant/bgStep.png"
                alt="Background"
                class="absolute inset-0 w-full h-full object-cover z-0"
            />

            <!-- Content Overlay -->
            <div class="relative z-10 w-full px-10 flex flex-col items-center justify-center h-full">
                <div class="mb-12 text-center text-balance px-4">
                    <h3
                        class="poppinsRegular text-white mb-5 tracking-tight leading-tight"
                        :style="{ fontSize: normalizeFontSize(20) }"
                    >
                        {{ $t('registration.testimonials_title') }}
                    </h3>
                </div>

                <!-- Carousel Container -->
                <div class="w-full max-w-4xl relative overflow-hidden">
                    <div
                        class="flex transition-transform duration-500 ease-out"
                        :style="{ transform: `translateX(-${currentSlide * 50}%)` }"
                    >
                        <div v-for="(testimonial, index) in testimonials" :key="index" class="w-1/2 flex-shrink-0 px-4">
                            <div class="relative p-8 rounded-[8px] text-white shadow-xl overflow-hidden">
                                <!-- Background Layer with Opacity -->
                                <div
                                    class="absolute inset-0 bg-no-repeat bg-cover bg-center opacity-10"
                                    style="background-image: url('/media/registerTenant/bgAvis.png')"
                                ></div>

                                <!-- Content Layer -->
                                <div class="relative z-10">
                                    <span class="text-[35px] mb-6" style="font-family: 'Inter'">“</span>
                                    <p class="poppinsBold text-[11px] mb-4">"{{ testimonial.quote }}"</p>
                                    <div class="text-amber-400 flex mb-4 justify-center">
                                        <svg
                                            v-for="i in 5"
                                            :key="i"
                                            width="12"
                                            height="12"
                                            viewBox="0 0 24 24"
                                            fill="currentColor"
                                            class="w-4 h-4"
                                        >
                                            <path
                                                d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"
                                            />
                                        </svg>
                                    </div>
                                    <div class="flex items-center gap-4">
                                        <img
                                            :src="testimonial.avatar"
                                            class="w-12 h-12 rounded-full object-cover border-2 border-white/30"
                                            :alt="testimonial.name"
                                        />
                                        <div>
                                            <h4 class="poppinsBold text-[11px]">{{ testimonial.name }}</h4>
                                            <p class="text-[10px] text-white/70 capitalize tracking-widest poppinsBold">
                                                {{ testimonial.role }}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Dots -->
                <div class="inline-flex space-x-3 mt-10">
                    <button
                        v-for="(testimonial, index) in testimonials"
                        :key="index"
                        @click="goToSlide(index)"
                        class="w-2.5 h-2.5 rounded-full transition-all duration-300"
                        :class="currentSlide === index ? 'bg-white w-8' : 'bg-white/40 hover:bg-white/60'"
                    ></button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import { debounce } from 'lodash'
import bg2Step from './bg2Step.vue'
import LanguageSwitcher from '../Parts/LanguageSwitcher.vue'
import normalizeSize from '../../../utils/normalizeSize'
import normalizeFontSize from '../../../utils/normalizeFontSize'
import { theme } from '../../../utils/theme'
export default {
    name: 'AccountStep',
    components: {
        bg2Step,
        LanguageSwitcher,
    },
    props: {
        formData: {
            type: Object,
            required: true,
        },
        isSubmitting: {
            type: Boolean,
            default: false,
        },
        serverErrors: {
            type: Object,
            default: () => ({}),
        },
    },
    data() {
        return {
            localFormData: {
                subdomain: this.formData.subdomain || '',
                password: this.formData.password || '',
                passwordConfirmation: this.formData.passwordConfirmation || '',
                acceptTerms: this.formData.acceptTerms || false,
                acceptOffers: this.formData.acceptOffers || false,
            },
            showPassword: false,
            showConfirmPassword: false,
            errors: {},
            isCheckingSubdomain: false,
            currentSlide: 0,
            autoplayInterval: null,
        }
    },
    mounted() {
        this.startAutoplay()
    },
    beforeDestroy() {
        this.stopAutoplay()
    },
    computed: {
        testimonials() {
            return [
                {
                    quote: this.$t('registration.reviews.quote1'),
                    name: this.$t('registration.reviews.author1'),
                    role: this.$t('registration.reviews.role1'),
                    avatar: 'https://randomuser.me/api/portraits/men/32.jpg',
                },
                {
                    quote: this.$t('registration.reviews.quote2'),
                    name: this.$t('registration.reviews.author2'),
                    role: this.$t('registration.reviews.role2'),
                    avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
                },
                {
                    quote: this.$t('registration.reviews.quote3'),
                    name: this.$t('registration.reviews.author3'),
                    role: this.$t('registration.reviews.role3'),
                    avatar: 'https://randomuser.me/api/portraits/women/26.jpg',
                },
                {
                    quote: this.$t('registration.reviews.quote4'),
                    name: this.$t('registration.reviews.author4'),
                    role: this.$t('registration.reviews.role4'),
                    avatar: 'https://randomuser.me/api/portraits/men/12.jpg',
                },
            ]
        },
        isDark() {
            return theme.mode === 'dark'
        },
        isFormValid() {
            const hasSubdomain = this.localFormData.subdomain && this.localFormData.subdomain.length >= 3
            const hasPassword = this.localFormData.password && this.localFormData.password.length >= 8
            const passwordsMatch = this.localFormData.password === this.localFormData.passwordConfirmation
            const hasAcceptedTerms = this.localFormData.acceptTerms
            const noSubdomainError = !this.errors.subdomain

            return hasSubdomain && hasPassword && passwordsMatch && hasAcceptedTerms && noSubdomainError
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        onSubdomainInput(e) {
            if (this.localFormData.subdomain) {
                this.localFormData.subdomain = this.localFormData.subdomain.toLowerCase()
            }
            this.validateSubdomain()
        },
        validateSubdomain: debounce(async function () {
            if (this.localFormData.subdomain) {
                this.localFormData.subdomain = this.localFormData.subdomain.toLowerCase()
            }
            const subdomain = this.localFormData.subdomain.trim().toLowerCase()
            this.errors.subdomain = ''

            if (!subdomain) return
            if (subdomain.length < 3) return

            const subdomainRegex = /^[a-z0-9](?:[a-z0-9\-]{0,28}[a-z0-9])?$/
            if (!subdomainRegex.test(subdomain)) {
                this.errors.subdomain = this.$t('registration.validation.subdomain_format')
                return
            }

            this.isCheckingSubdomain = true
            try {
                const response = await axios.post('/register-tenant/check-domain', { subdomain })
                if (!response.data.available) {
                    this.errors.subdomain = response.data.message || this.$t('registration.validation.subdomain_unavailable')
                }
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.isCheckingSubdomain = false
            }
        }, 500),
        validatePasswords() {
            this.errors.password = ''
            this.errors.passwordConfirmation = ''

            if (this.localFormData.password && this.localFormData.password.length < 8) {
                this.errors.password = this.$t('registration.validation.password_length')
            }

            if (
                this.localFormData.password &&
                this.localFormData.passwordConfirmation &&
                this.localFormData.password !== this.localFormData.passwordConfirmation
            ) {
                this.errors.passwordConfirmation = this.$t('registration.validation.password_mismatch')
            }
        },
        goBack() {
            this.$emit('prev-step')
        },
        submitForm() {
            if (!this.isFormValid) return
            this.$emit('update:formData', this.localFormData)
            this.$emit('submit')
        },
        goToSlide(index) {
            this.currentSlide = index
            this.resetAutoplay()
        },
        nextSlide() {
            this.currentSlide = (this.currentSlide + 1) % this.testimonials.length
        },
        startAutoplay() {
            this.autoplayInterval = setInterval(this.nextSlide, 5000)
        },
        stopAutoplay() {
            if (this.autoplayInterval) {
                clearInterval(this.autoplayInterval)
                this.autoplayInterval = null
            }
        },
        resetAutoplay() {
            this.stopAutoplay()
            this.startAutoplay()
        },
    },
}
</script>

<style scoped>
.font-sans {
    font-family: 'Poppins', sans-serif;
}

.text-primary {
    color: #7d2dbd;
}

.glass-blob {
    position: absolute;
    border-radius: 50%;
    filter: blur(120px);
    opacity: 0.5;
    animation: move 25s infinite alternate cubic-bezier(0.45, 0, 0.55, 1);
}

.blob-1 {
    width: 700px;
    height: 700px;
    background: #2563eb;
    top: -150px;
    left: -100px;
}
.blob-2 {
    width: 600px;
    height: 600px;
    background: #f97316;
    bottom: -100px;
    right: -50px;
    opacity: 0.3;
    animation-delay: -7s;
}
.blob-3 {
    width: 500px;
    height: 500px;
    background: #3b82f6;
    top: 30%;
    left: 30%;
    animation-delay: -14s;
}

@keyframes move {
    from {
        transform: translate(0, 0) scale(1) rotate(0deg);
    }
    to {
        transform: translate(15%, 20%) scale(1.1) rotate(10deg);
    }
}

.testimonial-summary-card {
    transition: all 0.5s cubic-bezier(0.4, 0, 0.2, 1);
}
</style>
